<script setup>
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SalesTransactionDocumentsAPI from 'dashboard/api/salesTransactionDocuments';
import SalesTransactionPartyDocumentsAPI from 'dashboard/api/salesTransactionPartyDocuments';

const props = defineProps({
  documents: { type: Array, default: () => [] },
  title: { type: String, required: true },
  transactionId: { type: [String, Number], default: null },
});

const { t } = useI18n();
const notify = useAlert;
const emptyValue = t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.EMPTY_VALUE');
const documentTypeLabel = document => {
  const types = document.document_types
    ?.map(type => type.name || type.nome || type.nome_tipo || type)
    .filter(Boolean);
  return types?.join(', ') || emptyValue;
};

const download = async document => {
  try {
    if (document.source === 'evidence') {
      window.open(document.url, '_blank', 'noopener,noreferrer');
      return;
    }
    const response =
      document.source === 'party'
        ? await SalesTransactionPartyDocumentsAPI.signedUrl(
            props.transactionId,
            document.party_id,
            document.id
          )
        : await SalesTransactionDocumentsAPI.signedUrl(
            props.transactionId,
            document.id
          );
    window.open(response.data.data.url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    notify(
      error?.response?.data?.message ||
        t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.DOWNLOAD_ERROR')
    );
  }
};
</script>

<template>
  <section
    data-test="nucleo-request-documents"
    class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
  >
    <header class="border-b border-lais-border-soft px-5 py-4">
      <h2
        class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        {{ title }}
      </h2>
    </header>
    <div
      class="hidden grid-cols-[minmax(8rem,0.35fr)_minmax(8rem,0.35fr)_minmax(0,1fr)] gap-4 bg-lais-bg-soft px-5 py-3 text-xs font-semibold uppercase text-lais-text-muted sm:grid"
    >
      <span>{{ t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.TYPE') }}</span>
      <span>
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.IDENTIFICATION') }}
      </span>
      <span>
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.FILENAME') }}
      </span>
    </div>
    <div v-if="documents.length" class="divide-y divide-lais-border-soft">
      <button
        v-for="document in documents"
        :key="`${document.source}-${document.id}`"
        type="button"
        class="grid w-full gap-2 px-5 py-3 text-left text-sm hover:bg-lais-primary-50 sm:grid-cols-[minmax(8rem,0.35fr)_minmax(8rem,0.35fr)_minmax(0,1fr)] sm:gap-4"
        @click="download(document)"
      >
        <span class="text-lais-text">
          <span class="mr-2 text-xs text-lais-text-muted sm:hidden">
            {{ t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.TYPE') }}
          </span>
          {{ documentTypeLabel(document) }}
        </span>
        <span class="text-lais-text-body">
          <span class="mr-2 text-xs text-lais-text-muted sm:hidden">
            {{
              t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.IDENTIFICATION')
            }}
          </span>
          {{ document.identification || emptyValue }}
        </span>
        <span class="truncate font-semibold text-lais-link">
          <span class="mr-2 text-xs font-normal text-lais-text-muted sm:hidden">
            {{ t('NUCLEO_WORK_ORDERS.DETAIL.REQUEST_DOCUMENTS.FILENAME') }}
          </span>
          {{ document.filename || emptyValue }}
        </span>
      </button>
    </div>
    <div
      v-else
      data-test="nucleo-request-documents-empty"
      class="grid gap-2 px-5 py-3 text-sm text-lais-text-body sm:grid-cols-3 sm:gap-4"
    >
      <span>{{ emptyValue }}</span>
      <span>{{ emptyValue }}</span>
      <span>{{ emptyValue }}</span>
    </div>
  </section>
</template>
